import { NIGHTMARES } from '@somnhold/content';
import { cellAtWorld } from '../grid.ts';
import { dist2, iatan2, idiv, imax, imin, isqrt, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Enemy } from '../types.ts';
import type { Pt } from '../caza-types.ts';
import type { SealHowler, SealRules, SealState } from '../isla-types.ts';
import { damageHero, ev, spawnEnemy } from './combat.ts';
import { stepToward } from './enemies.ts';
import { segDist2 } from './isla-layout.ts';
import { drown, hasCell, hitKept, lineCells } from './isla-ice.ts';

/**
 * Sigrun's last night (docs/sendas/barbaro.md, chapter 4, act 3, the memory and the test: «La última noche de Sigrun»;
 * the ground in battle/isla-layout.ts). Played as Sigrun, seventy years back, the thirty-first night: the White Island
 * under a green and red aurora, her camp whole with its fire and the stone with thirty strokes; out there the pack's
 * eyes, and the Grey Wolf circling in the water without coming in.
 *
 * The Ash Seal: a seal of ash, huge and grey, slow on the ice. It bites; and every so often it stands before what it
 * wants to ram (the hero, if near; else the fire), its line marked three seconds, then slides belly-down straight along
 * it: the hero on the line takes a third of its life, the fire on it is out at once, the wolves on it are gone, and the
 * ice it cut takes the wolves that step on it for a while. The clever thing is to make it slide where it suits you:
 * stand so its line to you doesn't pass the fire, and step off. Hurt, it goes for an open crack to heal (as the old
 * howler went for the dark water): wait at the crack, or keep on it. Two howlers with the pack: cut their howl or more
 * come. One wolf in three goes for the fire. Won with the Seal down and the fire lit; lost with the fire out (25 blows,
 * or the slide) or Sigrun down («no fue así»).
 *
 * Cost: one nightmare of its own, two howlers, the pack; the slide's cut a short sorted list.
 */

const sr = (s: BattleState): SealRules | undefined => s.rules?.senda?.isla?.seal;
const ss = (s: BattleState): SealState | undefined => s.senda?.isla?.seal;

const fresh = (s: BattleState, r: SealRules): SealState => {
  const e = spawnEnemy(s, 'wolf', 1, false, 0, r.start.x, r.start.y, r.hp);
  const howlers: SealHowler[] = r.howlers.map((p, k) => {
    const x = spawnEnemy(s, 'wolf', 1, false, 0, p.x, p.y, r.howler.hp);
    return { id: x.id, phase: 0, until: 0, next: r.howler.first + k * idiv(r.howler.every, 2), hpAt: x.hp };
  });
  return {
    id: e.id,
    phase: 0,
    until: 0,
    from: { x: 0, y: 0 },
    to: { x: 0, y: 0 },
    atFire: 0,
    hole: -1,
    hits: 0,
    hpWas: e.hp,
    fleeAt: e.maxHp - mulBp(e.maxHp, r.fleeBp),
    mark: e.maxHp,
    cd: 0,
    slideNext: r.slide.first,
    cut: [],
    cutUntil: -1,
    slides: 0,
    cuts: 0,
    dives: 0,
    drowned: 0,
    howlers,
    next: r.pack.first,
    gnawCd: r.gnaw,
    out: -1,
    done: -1,
  };
};

/** The Seal's state, if this nightmare is it. */
export const sealOf = (s: BattleState, id: number): SealState | undefined => {
  const w = ss(s);
  return w && w.id === id ? w : undefined;
};

/** One of the howlers, if this nightmare is one. */
export const sealHowlerOf = (s: BattleState, id: number): SealHowler | undefined => ss(s)?.howlers.find((x) => x.id === id);

/** The open crack nearest a point. */
export const sealNearestHole = (r: SealRules, x: number, y: number): number => {
  let best = 0;
  let bd = 0x7fffffff;
  r.holes.forEach((p, k) => {
    const d = dist2(p.x, p.y, x, y);
    if (d < bd) {
      bd = d;
      best = k;
    }
  });
  return best;
};

/** Where a slide from `a` toward `b` ends (its full length, kept on the island). */
export const slideEnd = (r: SealRules, a: Pt, b: Pt): Pt => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const d = imax(1, isqrt(dx * dx + dy * dy));
  let x = a.x + idiv(dx * r.slide.len, d);
  let y = a.y + idiv(dy * r.slide.len, d);
  // Kept inside the ice's ring (the board's 10 hexes).
  const m = isqrt(x * x + y * y);
  if (m > 9800) {
    x = idiv(x * 9800, m);
    y = idiv(y * 9800, m);
  }
  return { x, y };
};

/** Whether the fire stands on a line (within its width). */
export const slideHitsFire = (r: SealRules, a: Pt, b: Pt): boolean => segDist2(r.fire.x, r.fire.y, a, b) <= r.slide.width * r.slide.width;

const cutOff = (s: BattleState, r: SealRules, w: SealState, e: Enemy, how: number, cue: (c: string) => void): void => {
  w.phase = 6;
  w.until = s.tick + r.stun;
  w.cuts++;
  w.mark = e.hp;
  e.stun = imax(e.stun, r.stun);
  w.slideNext = imax(w.slideNext, s.waveT + r.slide.after);
  ev(s, { t: 'senda', kind: 'iCut', npc: '', x: e.x, y: e.y, n: how });
  cue(`cut${w.cuts}`);
};

const howlersTick = (s: BattleState, r: SealRules, w: SealState, cue: (c: string) => void): void => {
  const H = r.howler;
  for (const hw of w.howlers) {
    const e = s.enemies.find((x) => x.id === hw.id);
    if (!e || e.hp <= 0) {
      hw.phase = 3;
      continue;
    }
    if (hw.phase === 0 && s.waveT >= hw.next && e.stun === 0) {
      hw.phase = 1;
      hw.until = s.tick + H.rear;
      hw.hpAt = e.hp;
      ev(s, { t: 'senda', kind: 'iRear', npc: '', x: e.x, y: e.y, n: H.rear });
      cue('rear1');
    } else if (hw.phase === 1 && s.tick >= hw.until) {
      hw.phase = 2;
      hw.until = s.tick + H.howl;
      ev(s, { t: 'senda', kind: 'iHowl', npc: '', x: e.x, y: e.y, n: H.howl });
    }
    if (hw.phase === 1 || hw.phase === 2) {
      if (hw.hpAt - e.hp >= mulBp(e.maxHp, H.cutBp)) {
        hw.phase = 0;
        hw.next = s.waveT + H.every;
        e.stun = imax(e.stun, H.stun);
        ev(s, { t: 'senda', kind: 'iHowlCut', npc: '', x: e.x, y: e.y });
        cue('howlCut1');
      } else if (hw.phase === 2 && s.tick >= hw.until) {
        hw.phase = 0;
        hw.next = s.waveT + H.every;
        const p = r.rim[rngInt(s.rng, r.rim.length)] as Pt;
        for (let i = 0; i < H.raise; i++) spawnEnemy(s, 'wolf', 1, false, 0, p.x + ((i % 3) - 1) * 450, p.y + (i >> 1) * 400, r.wolfHp);
        cue('howled1');
      }
    }
  }
};

/** The slide, one tick of it: along the line, taking what it crosses. */
const slideStep = (s: BattleState, r: SealRules, w: SealState, e: Enemy, cue: (c: string) => void): void => {
  const a = { x: e.x, y: e.y };
  const done = stepToward(e, w.to.x, w.to.y, r.slide.pace);
  const b = { x: e.x, y: e.y };
  const W2 = r.slide.width * r.slide.width;
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (h && w.hits === 0 && segDist2(h.x, h.y, a, b) <= W2) {
    w.hits = 1;
    damageHero(s, h, mulBp(h.stats.maxHp, r.slide.bp), e, 'physical', true);
  }
  if (w.out < 0 && segDist2(r.fire.x, r.fire.y, a, b) <= W2) {
    const f = s.senda?.npcs.find((p) => p.id === 'fire');
    if (f) hitKept(s, 'fire', f.hp, r.fire.x, r.fire.y, 'iFireHit');
    w.out = s.tick;
    ev(s, { t: 'senda', kind: 'iFireOut', npc: 'fire', x: r.fire.x, y: r.fire.y });
    cue('fireOut');
  }
  for (const x of s.enemies) {
    if (x.hp <= 0 || x.id === e.id || NIGHTMARES[x.kind].flying || w.howlers.some((hw) => hw.id === x.id)) continue;
    if (segDist2(x.x, x.y, a, b) <= W2) {
      drown(s, x, h);
      w.drowned++;
    }
  }
  if (done) {
    w.phase = 0;
    w.hits = 0;
    w.cut = lineCells(s, w.from, w.to);
    w.cutUntil = s.tick + r.slide.crack;
    w.slideNext = s.waveT + rngRange(s.rng, r.slide.every[0], r.slide.every[1]);
    ev(s, { t: 'senda', kind: 'iSlide', npc: '', x: w.to.x, y: w.to.y, n: w.slides });
  }
};

export const sealTick = (s: BattleState, cue: (c: string) => void): void => {
  const r = sr(s);
  const st = s.senda;
  if (!r || !st) return;
  const is = (st.isla ??= {});
  const w = (is.seal ??= fresh(s, r));
  if (!s.started || w.done >= 0 || w.out >= 0) return;
  const e = s.enemies.find((x) => x.id === w.id);
  if (!e || e.hp <= 0) {
    w.done = s.tick;
    cue('sealDown');
    return;
  }
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (w.cd > 0) w.cd--;
  // The cut ice: wolves that step on it go in.
  if (w.cutUntil >= 0) {
    if (s.tick >= w.cutUntil) {
      w.cut = [];
      w.cutUntil = -1;
    } else
      for (const x of s.enemies) {
        if (x.hp <= 0 || x.kind !== 'wolf' || x.id === w.id || w.howlers.some((hw) => hw.id === x.id)) continue;
        if (!hasCell(w.cut, cellAtWorld(s, x.x, x.y))) continue;
        drown(s, x, h);
        w.drowned++;
        ev(s, { t: 'senda', kind: 'iDrown', npc: '', x: x.x, y: x.y });
        if (w.drowned === 1) cue('drown1');
      }
  }
  if (w.phase === 0 && e.hp <= w.fleeAt && e.stun === 0) {
    // Hurt: it looks at the nearest open crack.
    w.phase = 3;
    w.until = s.tick + r.look;
    w.hole = sealNearestHole(r, e.x, e.y);
    w.fleeAt = imax(1, w.fleeAt - mulBp(e.maxHp, r.fleeBp));
    const p = r.holes[w.hole] as Pt;
    ev(s, { t: 'senda', kind: 'iLook', npc: '', x: p.x, y: p.y, n: w.hole });
    cue(`look${w.dives + w.cuts + 1}`);
  } else if (w.phase === 0 && s.waveT >= w.slideNext && e.stun === 0) {
    // It stands before what it wants to ram: the hero near, else the fire.
    const near = h && withinRange(h.x, h.y, e.x, e.y, r.aggro);
    const t = near && h ? { x: h.x, y: h.y } : r.fire;
    w.phase = 1;
    w.until = s.tick + r.slide.warn;
    w.from = { x: e.x, y: e.y };
    w.to = slideEnd(r, w.from, t);
    w.atFire = near ? 0 : 1;
    w.slides++;
    e.facing = iatan2(w.to.y - e.y, w.to.x - e.x);
    ev(s, { t: 'senda', kind: 'iAim', npc: '', x: w.to.x, y: w.to.y, n: w.atFire });
    cue(`slide${w.slides}`);
  } else if (w.phase === 1 && w.atFire === 1 && h && withinRange(h.x, h.y, e.x, e.y, r.aggro >> 1)) {
    // Aiming at the fire, the hero comes at it: it turns to the hero (its line marked again, the whole warning).
    w.until = s.tick + r.slide.warn;
    w.to = slideEnd(r, w.from, { x: h.x, y: h.y });
    w.atFire = 0;
    e.facing = iatan2(w.to.y - e.y, w.to.x - e.x);
    ev(s, { t: 'senda', kind: 'iAim', npc: '', x: w.to.x, y: w.to.y, n: 0 });
  } else if (w.phase === 1 && s.tick >= w.until) {
    w.phase = 2;
    w.hits = 0;
  } else if (w.phase === 2) slideStep(s, r, w, e, cue);
  else if (w.phase === 3 && s.tick >= w.until) {
    w.phase = 4;
    w.hits = 0;
    w.hpWas = e.hp;
    ev(s, { t: 'senda', kind: 'iGo', npc: '', x: e.x, y: e.y, n: w.hole });
  } else if (w.phase === 4) {
    if (e.hp < w.hpWas) w.hits++;
    w.hpWas = e.hp;
    const p = r.holes[w.hole] as Pt;
    if (w.hits >= r.caught) cutOff(s, r, w, e, 1, cue);
    else if (withinRange(e.x, e.y, p.x, p.y, 800)) {
      if (h && withinRange(h.x, h.y, p.x, p.y, r.block)) cutOff(s, r, w, e, 0, cue);
      else {
        w.phase = 5;
        w.until = s.tick + r.under;
        w.dives++;
        w.hpWas = e.hp;
        e.x = p.x;
        e.y = p.y;
        ev(s, { t: 'senda', kind: 'iDive', npc: '', x: p.x, y: p.y, n: w.hole });
        cue(`dive${w.dives}`);
      }
    }
  } else if (w.phase === 5) {
    // Under the ice nothing reaches it.
    e.hp = imax(e.hp, w.hpWas);
    if (s.tick >= w.until) {
      const p = r.holes[w.hole] as Pt;
      e.hp = imin(e.maxHp, imax(e.hp, w.mark) + mulBp(e.maxHp, r.healBp));
      w.mark = e.hp;
      w.fleeAt = imax(1, e.hp - mulBp(e.maxHp, r.fleeBp));
      w.phase = 0;
      w.hpWas = e.hp;
      // Up onto the ice by the crack, toward the middle.
      const d = imax(1, isqrt(p.x * p.x + p.y * p.y));
      e.x = p.x - idiv(p.x * 1100, d);
      e.y = p.y - idiv(p.y * 1100, d);
      for (let i = 0; i < r.raise; i++) spawnEnemy(s, 'wolf', 1, false, 0, e.x + ((i % 2) * 2 - 1) * 500, e.y + 400, r.wolfHp);
      w.slideNext = imax(w.slideNext, s.waveT + r.slide.after);
      ev(s, { t: 'senda', kind: 'iRise', npc: '', x: e.x, y: e.y, n: w.hole });
      cue(`rise${w.dives}`);
    }
  } else if (w.phase === 6 && s.tick >= w.until) {
    w.phase = 0;
    w.hpWas = e.hp;
  }
  howlersTick(s, r, w, cue);
  // The pack off the ice's rim; one in three for the fire.
  if (s.waveT >= w.next) {
    w.next = s.waveT + rngRange(s.rng, r.pack.every[0], r.pack.every[1]);
    const p = r.rim[rngInt(s.rng, r.rim.length)] as Pt;
    const n = rngRange(s.rng, r.pack.n[0], r.pack.n[1]);
    for (let i = 0; i < n; i++) spawnEnemy(s, 'wolf', 1, false, 0, p.x + ((i % 3) - 1) * 450, p.y + (i >> 1) * 400, r.wolfHp);
  }
  if (w.gnawCd > 0) w.gnawCd--;
  else {
    w.gnawCd = r.gnaw;
    let n = 0;
    for (const x of s.enemies) if (x.hp > 0 && fireGnawer(w, x) && withinRange(x.x, x.y, r.fire.x, r.fire.y, r.fireReach) && !(h && withinRange(h.x, h.y, x.x, x.y, r.gnawReach))) n++;
    if (n > 0 && hitKept(s, 'fire', n, r.fire.x, r.fire.y, 'iFireHit')) {
      w.out = s.tick;
      ev(s, { t: 'senda', kind: 'iFireOut', npc: 'fire', x: r.fire.x, y: r.fire.y });
      cue('fireOut');
    }
  }
};

/** One wolf in three of the pack goes for the fire (never a howler). */
const fireGnawer = (w: SealState, e: Enemy): boolean => e.kind === 'wolf' && e.id % 3 === 0 && e.id !== w.id && !w.howlers.some((hw) => hw.id === e.id);

/** The Seal and the howlers by their own rule; the fire's gnawers (true: the act moved it). */
export const sealHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const r = sr(s);
  const w = ss(s);
  if (!r || !w) return false;
  const h = s.heroes.find((x) => x.downTicks === 0);
  const hw = w.howlers.find((x) => x.id === e.id);
  if (hw) {
    if (hw.phase === 1 || hw.phase === 2) {
      e.anim = 'cast';
      return true;
    }
    return false;
  }
  if (e.id !== w.id) {
    if (e.stun > 0 || !fireGnawer(w, e) || (h && withinRange(h.x, h.y, e.x, e.y, r.gnawReach))) return false;
    if (withinRange(e.x, e.y, r.fire.x, r.fire.y, r.fireReach - 200)) {
      e.facing = iatan2(r.fire.y - e.y, r.fire.x - e.x);
      if (e.anim !== 'attack' || e.animT > 10) {
        e.anim = 'attack';
        e.animT = 0;
      }
      return true;
    }
    e.anim = 'walk';
    stepToward(e, r.fire.x, r.fire.y, px(e));
    return true;
  }
  if (w.phase === 5) {
    e.anim = 'idle';
    return true;
  }
  if (w.phase === 6 || e.stun > 0) return false;
  if (w.phase === 1 || w.phase === 3) {
    const t = w.phase === 1 ? w.to : (r.holes[w.hole] as Pt);
    e.facing = iatan2(t.y - e.y, t.x - e.x);
    e.anim = 'cast';
    return true;
  }
  if (w.phase === 2) {
    e.anim = 'walk';
    return true;
  }
  if (w.phase === 4) {
    const p = r.holes[w.hole] as Pt;
    e.anim = 'walk';
    stepToward(e, p.x, p.y, idiv(r.speed * 3, 2 * 12));
    return true;
  }
  if (!h) {
    e.anim = 'idle';
    return true;
  }
  if (!withinRange(e.x, e.y, h.x, h.y, r.reach)) {
    e.anim = 'walk';
    stepToward(e, h.x, h.y, idiv(r.speed, 12));
    return true;
  }
  if (h.x !== e.x || h.y !== e.y) e.facing = iatan2(h.y - e.y, h.x - e.x);
  if (w.cd === 0) {
    w.cd = r.reload;
    e.anim = 'attack';
    e.animT = 0;
    damageHero(s, h, mulBp(h.stats.maxHp, r.biteBp), e, 'physical', false);
  } else if (e.anim !== 'attack' || e.animT > 6) e.anim = 'idle';
  return true;
};

/** The Seal's life taken, of all of it. */
export const sealGoal = (s: BattleState): { done: number; need: number } => {
  const w = ss(s);
  const e = w ? s.enemies.find((x) => x.id === w.id) : undefined;
  if (!w || !e) return { done: w && w.done >= 0 ? 1 : 0, need: 1 };
  return { done: e.maxHp - imax(0, e.hp), need: e.maxHp };
};

export const sealResult = (s: BattleState): 'won' | 'lost' | null => {
  const w = ss(s);
  if (!w) return null;
  if (w.out >= 0 && s.tick >= w.out + 24) return 'lost';
  return w.done >= 0 && s.tick >= w.done + 24 ? 'won' : null;
};
